import { useState, useCallback } from 'react';
import { Car, Loader2, Plus, Wrench, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@flote/ui';
import type { VehicleStatus, VehicleType } from '@flote/types/delivery';
import type { Vehicle } from '@flote/contracts/http';
import { deliveryTsr } from '../../api';
import { VehicleFormModal, type VehicleFormData } from '../../components/vehicles';
import { getErrorMessage } from '../../lib/error-utils';

function VehicleStatusCard({
  title,
  count,
  icon: Icon,
  color,
  isLoading,
}: {
  title: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`rounded-full p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded bg-muted" />
          ) : (
            <p className="text-2xl font-bold">{count}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const STATUS_BADGES: Record<VehicleStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  available: { label: 'Available', variant: 'default' },
  assigned: { label: 'Assigned', variant: 'secondary' },
  maintenance: { label: 'Maintenance', variant: 'outline' },
  retired: { label: 'Retired', variant: 'destructive' },
};

const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  CAR: 'Car',
  TRUCK: 'Truck',
  MOTORCYCLE: 'Motorcycle',
  BICYCLE: 'Bicycle',
};

export function VehiclesPage() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deletingVehicle, setDeletingVehicle] = useState<Vehicle | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch vehicles list with filters
  const { data: vehiclesResponse, isLoading: vehiclesLoading, refetch: refetchVehicles } = deliveryTsr.adminVehicles.list.useQuery({
    queryKey: ['vehicles-list', statusFilter, typeFilter],
    queryData: {
      query: {
        status: statusFilter !== 'all' ? (statusFilter as VehicleStatus) : undefined,
        type: typeFilter !== 'all' ? (typeFilter as VehicleType) : undefined,
        limit: 100,
      },
    },
  });
  const vehicles = vehiclesResponse?.status === 200 ? vehiclesResponse.body : [];

  // Fetch teams for the form
  const { data: teamsResponse } = deliveryTsr.adminTeams.list.useQuery({
    queryKey: ['teams-list'],
  });
  const teams = teamsResponse?.status === 200 ? teamsResponse.body : [];

  // Mutations
  const createVehicleMutation = deliveryTsr.adminVehicles.create.useMutation();
  const updateVehicleMutation = deliveryTsr.adminVehicles.update.useMutation();
  const deleteVehicleMutation = deliveryTsr.adminVehicles.delete.useMutation();

  const handleCreateVehicle = useCallback(async (data: VehicleFormData) => {
    setIsCreating(true);
    try {
      const response = await createVehicleMutation.mutateAsync({
        body: data,
      });

      if (response.status === 201) {
        toast.success('Vehicle created successfully');
        setIsCreateModalOpen(false);
        refetchVehicles();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to create vehicle');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create vehicle'));
      console.error('Create vehicle error:', error);
    } finally {
      setIsCreating(false);
    }
  }, [createVehicleMutation, refetchVehicles]);

  const handleUpdateVehicle = useCallback(async (data: VehicleFormData) => {
    if (!editingVehicle) return;

    setIsEditing(true);
    try {
      const response = await updateVehicleMutation.mutateAsync({
        params: { vehicleId: editingVehicle.id },
        body: data,
      });

      if (response.status === 200) {
        toast.success('Vehicle updated successfully');
        setEditingVehicle(null);
        refetchVehicles();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to update vehicle');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update vehicle'));
      console.error('Update vehicle error:', error);
    } finally {
      setIsEditing(false);
    }
  }, [editingVehicle, updateVehicleMutation, refetchVehicles]);

  const handleDeleteVehicle = useCallback(async () => {
    if (!deletingVehicle) return;

    setIsDeleting(true);
    try {
      const response = await deleteVehicleMutation.mutateAsync({
        params: { vehicleId: deletingVehicle.id },
        body: undefined,
      });

      if (response.status === 204) {
        toast.success('Vehicle deleted successfully');
        setDeletingVehicle(null);
        refetchVehicles();
      } else {
        const errorBody = response.body as unknown as { message?: string } | undefined;
        toast.error(errorBody?.message || 'Failed to delete vehicle');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete vehicle'));
      console.error('Delete vehicle error:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [deletingVehicle, deleteVehicleMutation, refetchVehicles]);

  // Compute status counts
  const availableCount = vehicles.filter(v => v.status === 'available').length;
  const assignedCount = vehicles.filter(v => v.status === 'assigned').length;
  const maintenanceCount = vehicles.filter(v => v.status === 'maintenance').length;
  const retiredCount = vehicles.filter(v => v.status === 'retired').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Vehicles</h1>
          <p className="text-muted-foreground">
            Manage your fleet vehicles
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Vehicle
        </Button>
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <VehicleStatusCard
          title="Available"
          count={availableCount}
          icon={CheckCircle}
          color="bg-green-100 text-green-600"
          isLoading={vehiclesLoading}
        />
        <VehicleStatusCard
          title="Assigned"
          count={assignedCount}
          icon={Car}
          color="bg-blue-100 text-blue-600"
          isLoading={vehiclesLoading}
        />
        <VehicleStatusCard
          title="Maintenance"
          count={maintenanceCount}
          icon={Wrench}
          color="bg-yellow-100 text-yellow-600"
          isLoading={vehiclesLoading}
        />
        <VehicleStatusCard
          title="Retired"
          count={retiredCount}
          icon={XCircle}
          color="bg-slate-100 text-slate-400"
          isLoading={vehiclesLoading}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 sm:gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="CAR">Car</SelectItem>
            <SelectItem value="TRUCK">Truck</SelectItem>
            <SelectItem value="MOTORCYCLE">Motorcycle</SelectItem>
            <SelectItem value="BICYCLE">Bicycle</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Vehicles List */}
      <Card>
        <CardHeader>
          <CardTitle>Fleet Vehicles</CardTitle>
        </CardHeader>
        <CardContent>
          {vehiclesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : vehicles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Car className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No vehicles found</h3>
              <p className="max-w-sm text-sm text-muted-foreground">
                {statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'No vehicles match the current filters. Try adjusting your filters.'
                  : 'Add vehicles to your fleet to get started.'}
              </p>
            </div>
          ) : (
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="hidden sm:table-cell">License Plate</TableHead>
                      <TableHead className="hidden md:table-cell">Color</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Team</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicles.map((vehicle) => {
                      const statusBadge = STATUS_BADGES[vehicle.status];
                      const team = teams.find(t => t.id === vehicle.teamId);
                      return (
                        <TableRow key={vehicle.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Car className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">
                                {VEHICLE_TYPE_LABELS[vehicle.type]}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {vehicle.description || '—'}
                          </TableCell>
                          <TableCell className="hidden text-muted-foreground sm:table-cell">
                            {vehicle.licensePlate || '—'}
                          </TableCell>
                          <TableCell className="hidden text-muted-foreground md:table-cell">
                            {vehicle.color || '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                          </TableCell>
                          <TableCell className="hidden text-muted-foreground lg:table-cell">
                            {team?.name || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingVehicle(vehicle)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeletingVehicle(vehicle)}
                                disabled={vehicle.status === 'assigned'}
                              >
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Vehicle Modal */}
      <VehicleFormModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSubmit={handleCreateVehicle}
        isProcessing={isCreating}
        teams={teams}
      />

      {/* Edit Vehicle Modal */}
      <VehicleFormModal
        open={!!editingVehicle}
        onOpenChange={(open) => !open && setEditingVehicle(null)}
        onSubmit={handleUpdateVehicle}
        isProcessing={isEditing}
        teams={teams}
        vehicle={editingVehicle || undefined}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingVehicle} onOpenChange={(open) => !open && setDeletingVehicle(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this vehicle? This action cannot be undone.
              {deletingVehicle?.description && (
                <span className="mt-2 block font-medium text-foreground">
                  {deletingVehicle.description}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteVehicle}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
